/**
 * THE AI model dropdown — every model picker in Kola is this component.
 *
 * One list (the admin-managed one, ./store.ts), one first option ("Default ·
 * <model>", pre-selected), one label format, one remembered-pick rule. That
 * uniformity is the feature: before this, the sections and brainstorm pickers
 * dumped the provider's ~400-entry catalog with prices, the thumbnail panel
 * showed a hand-ranked subset baked into client code, and the admin page took
 * free text — three different answers to "which models can I use here?".
 *
 * DEFAULT IS THE DEFAULT. The first option is always the resolved default for
 * this function, and it is what an untouched picker sits on — so a run nobody
 * fiddled with follows whatever the admin has set, and changing the shared
 * default on /admin moves everyone at once. An explicit pick is remembered per
 * person in localStorage (./pick.ts); `onChange` is told about it either way.
 *
 * `kind` filters to text or image models, the one distinction a picker must
 * not get wrong (an image model on a text function returns nothing usable; a
 * text model on the thumbnail render fails every frame).
 *
 * Stops its own keydown events: these live inside board panels, where the
 * grid's global key handling would otherwise read a type-ahead as a shortcut.
 */
import type { AiFunctionKey, AiModelKind } from "@kola/shared";

import { defaultOptionLabel, entryLabel, withCurrentModel } from "./pick.js";
import { useAiModelDefault, useAiModelsLoaded, useAiModelsOfKind } from "./store.js";

export interface ModelSelectProps {
  /** Which AI function this picker chooses for — names the default. */
  functionKey: AiFunctionKey;
  /** Which half of the list to offer. */
  kind: AiModelKind;
  /** The current pick; `""` (DEFAULT_MODEL) means "use the default". */
  value: string;
  onChange: (model: string) => void;
  disabled?: boolean;
  /**
   * False on the /admin cards, which SET the default and so must not offer it
   * as a choice: picking "Default" there would mean "apply nothing". Every
   * other picker keeps it — it is the pre-selected first option.
   */
  includeDefault?: boolean;
  id?: string;
  /** Native title, for the panels that label the control by tooltip alone. */
  title?: string;
  className?: string;
}

export function ModelSelect({
  functionKey,
  kind,
  value,
  onChange,
  disabled = false,
  includeDefault = true,
  id,
  title,
  className,
}: ModelSelectProps) {
  const entries = useAiModelsOfKind(kind);
  const defaultModel = useAiModelDefault(functionKey);
  const loaded = useAiModelsLoaded();

  // The shared class always applies and a caller's is additive, so a panel can
  // size the control without opting out of the common look.
  const classes = className === undefined ? "ai-model-select" : `ai-model-select ${className}`;

  return (
    <select
      id={id}
      title={title}
      className={classes}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {/* Always first, always the fallback — even before the list loads, so
          the control is never empty and never briefly claims a wrong model. */}
      {includeDefault && (
        <option value="">{loaded ? defaultOptionLabel(defaultModel) : "Default"}</option>
      )}
      {withCurrentModel(entries, value).map((entry) => (
        <option key={entry.id} value={entry.modelId}>
          {entryLabel(entry)}
        </option>
      ))}
    </select>
  );
}
